/*
 * Beeldvergelijking (Best in Light, 17-10).
 * Zelfde element <image-comparison> als het thema, met een nieuwe bediening:
 * - slepen met muis, aanraking of pen op het hele beeld (Pointer Events met
 *   pointer capture, dus loslaten buiten het beeld en afgebroken aanrakingen
 *   worden netjes afgehandeld);
 * - op aanraakschermen pas schuiven bij een duidelijk horizontale beweging,
 *   zodat verticaal scrollen over het beeld blijft werken;
 * - de greep is een schuifregelaar (role="slider") met pijltoetsen,
 *   Home/End en PageUp/PageDown, en een actuele waarde voor hulptechnologie;
 * - start op 50/50, geen demonstratiebeweging;
 * - labels verdwijnen alleen als hun beeldhelft te smal wordt.
 */
if (!customElements.get('image-comparison')) {
  customElements.define(
    'image-comparison',
    class ImageComparison extends HTMLElement {
      connectedCallback() {
        if (this._bilInit) return;
        this._bilInit = true;

        this.handle = this.querySelector('.image-comparison__button');
        if (!this.handle) return;
        this.isHorizontal = this.dataset.direction !== 'vertical';
        this.isRTL = document.documentElement.dir === 'rtl' || (window.FoxTheme && FoxTheme.config && FoxTheme.config.isRTL);
        this.labels = [
          this.querySelector('.image-comparison__before .image-comparison__heading'),
          this.querySelector('.image-comparison__after .image-comparison__heading'),
        ];
        this.naam1 = this.getAttribute('data-label-1') || '';
        this.naam2 = this.getAttribute('data-label-2') || '';
        this.waarde = 50;
        this.sleep = null;

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);
        this.onKey = this.onKey.bind(this);

        this.addEventListener('pointerdown', this.onDown);
        this.addEventListener('pointermove', this.onMove);
        this.addEventListener('pointerup', this.onUp);
        this.addEventListener('pointercancel', this.onUp);
        this.addEventListener('lostpointercapture', this.onUp);
        this.addEventListener('dragstart', (e) => e.preventDefault());
        this.handle.addEventListener('keydown', this.onKey);

        if ('ResizeObserver' in window) {
          this.ro = new ResizeObserver(() => this.zet(this.waarde));
          this.ro.observe(this);
        } else {
          window.addEventListener('resize', () => this.zet(this.waarde));
        }
        this.zet(50);
      }

      disconnectedCallback() {
        if (this.ro) this.ro.disconnect();
        this._bilInit = false;
      }

      /* Kleinste en grootste stand: de greep blijft altijd helemaal in beeld. */
      grenzen() {
        const maat = this.isHorizontal ? this.clientWidth : this.clientHeight;
        if (!maat) return [0, 100];
        const r = ((this.handle.querySelector('span') || this.handle).offsetWidth || 44) / 2 + 2;
        const min = Math.min(50, (r / maat) * 100);
        return [min, 100 - min];
      }

      zet(p) {
        const [min, max] = this.grenzen();
        this.waarde = Math.max(min, Math.min(max, p));
        this.style.setProperty('--percent', this.waarde + '%');
        const links = Math.round(this.waarde);
        this.handle.setAttribute('aria-valuenow', String(links));
        this.handle.setAttribute('aria-valuetext', this.naam1 + ' ' + links + '%, ' + this.naam2 + ' ' + (100 - links) + '%');
        this.labelsBijwerken();
      }

      labelsBijwerken() {
        if (!this.isHorizontal) return;
        const breed = this.clientWidth;
        if (!breed) return;
        const lijn = (this.waarde / 100) * breed;
        const helften = this.isRTL ? [breed - lijn, lijn] : [lijn, breed - lijn];
        this.labels.forEach((l, i) => {
          if (!l) return;
          /* label + buitenrand + een kleine marge tot de lijn moeten passen.
             De labels staan boven of onder, niet op de hoogte van de greep. */
          const rand = parseFloat(getComputedStyle(this).getPropertyValue('--heading-offset-x')) || 10;
          const nodig = l.offsetWidth + rand + 6;
          l.classList.toggle('is-te-smal', helften[i] < nodig);
        });
      }

      positie(e) {
        const r = this.getBoundingClientRect();
        let p = this.isHorizontal ? ((e.clientX - r.left) / r.width) * 100 : ((e.clientY - r.top) / r.height) * 100;
        if (this.isHorizontal && this.isRTL) p = 100 - p;
        return p;
      }

      onDown(e) {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        const opGreep = !!e.target.closest('.image-comparison__button');
        this.sleep = { id: e.pointerId, x: e.clientX, y: e.clientY, actief: false };
        /* Muis en pen: direct schuiven. Aanraking: alleen direct op de greep;
           elders eerst afwachten of de beweging horizontaal is. */
        if (e.pointerType !== 'touch' || opGreep) {
          this.begin(e);
          if (e.pointerType !== 'touch') this.zet(this.positie(e));
          if (opGreep) this.handle.focus({ preventScroll: true });
        }
      }

      begin(e) {
        this.sleep.actief = true;
        this.classList.add('is-dragging');
        try { this.setPointerCapture(e.pointerId); } catch (err) {}
        if (e.cancelable) e.preventDefault();
      }

      onMove(e) {
        const s = this.sleep;
        if (!s || s.id !== e.pointerId) return;
        if (!s.actief) {
          const dx = Math.abs(e.clientX - s.x);
          const dy = Math.abs(e.clientY - s.y);
          const langs = this.isHorizontal ? dx : dy;
          const dwars = this.isHorizontal ? dy : dx;
          if (dwars > 8 && dwars > langs) { this.sleep = null; return; } /* gewoon scrollen */
          if (langs < 8 || langs < dwars * 1.2) return;
          this.begin(e);
        }
        if (e.cancelable) e.preventDefault();
        this.zet(this.positie(e));
      }

      onUp(e) {
        if (!this.sleep || (e.pointerId !== undefined && this.sleep.id !== e.pointerId)) return;
        this.sleep = null;
        this.classList.remove('is-dragging');
        try { if (this.hasPointerCapture(e.pointerId)) this.releasePointerCapture(e.pointerId); } catch (err) {}
      }

      onKey(e) {
        const stap = e.shiftKey ? 10 : 2;
        let p = this.waarde;
        const terug = this.isRTL && this.isHorizontal ? -1 : 1;
        switch (e.key) {
          case 'ArrowLeft': p -= stap * terug; break;
          case 'ArrowRight': p += stap * terug; break;
          case 'ArrowDown': p -= stap; break;
          case 'ArrowUp': p += stap; break;
          case 'PageUp': p -= 10; break;
          case 'PageDown': p += 10; break;
          case 'Home': p = 0; break;
          case 'End': p = 100; break;
          default: return;
        }
        e.preventDefault();
        this.zet(p);
      }
    }
  );
}
